import React, { useEffect, useState, useCallback } from 'react';
import { userService } from '../../services/adminServices';
import { RefreshCw, Users, CheckCircle } from 'lucide-react';

// Define the types for your state
interface CityWaitlistInfo {
  _id: string | null; // City name or null
  count: number;
}

interface WaitlistUser {
    _id: string;
    username: string;
    email: string;
    // Add other fields if you use them: name, homeCity, createdAt
}


function WaitlistByCityPanel() {
  // Use the defined types with useState
  const [cities, setCities] = useState<CityWaitlistInfo[]>([]);
  const [selectedCity, setSelectedCity] = useState<string | null>(null); // City name is a string
  const [users, setUsers] = useState<WaitlistUser[]>([]);
  const [loadingCities, setLoadingCities] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [activatingCity, setActivatingCity] = useState<string | null>(null); // Track activation state
  const [error, setError] = useState<string | null>(null); // Add error state

  const fetchCities = useCallback(() => {
      setLoadingCities(true);
      setError(null); // Clear previous errors
      userService.getWaitlistCities()
        .then(data => {
          if (Array.isArray(data?.cities)) {
              setCities(data.cities);
          } else {
              console.error("Invalid data structure received for cities:", data);
              setCities([]); // Set to empty array on invalid data
              setError("Failed to load city data format.");
          }
        })
        .catch(err => {
            console.error("Error fetching cities:", err);
            setError(err.message || "Failed to load cities.");
        })
        .finally(() => setLoadingCities(false));
  }, []); // Empty dependency array, fetch only once or on manual refresh

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  const handleViewUsers = (city: string | null) => { // Type the city parameter
    if (!city || loadingUsers) return; // Don't fetch if city is null/undefined or already loading
    setSelectedCity(city);
    setLoadingUsers(true);
    setError(null);
    userService.getWaitlistUsersByCity(city)
      .then(data => {
        if (Array.isArray(data?.users)) {
            setUsers(data.users);
        } else {
            console.error("Invalid data structure received for users:", data);
            setUsers([]);
            setError("Failed to load user data format.");
        }
      })
      .catch(err => {
          console.error(`Error fetching users for ${city}:`, err);
          setError(err.message || `Failed to load users for ${city}.`);
          setUsers([]); // Clear users on error
      })
      .finally(() => setLoadingUsers(false));
  };

  const handleActivateAll = async (city: string | null) => { // Type the city parameter
    if (!city || activatingCity) { // Prevent activation if no city or already activating
        return;
    }
    if (!window.confirm(`Activate all ${cities.find(c=>c._id === city)?.count || ''} waitlisted users in ${city}? This cannot be undone.`)) return;

    setActivatingCity(city); // Set loading state for this specific city
    setError(null);

    try {
        await userService.activateCityWaitlist(city);
        // Success feedback can be added here (e.g., using a toast notification library)
        alert(`Activation process started successfully for ${city}.`);

        // Refresh city counts and potentially the user list after activation
        fetchCities(); // Re-fetch city counts to update the numbers
        if (selectedCity === city) {
            // If viewing the activated city, clear the user list or re-fetch
            setUsers([]); // Clear immediately, or re-fetch after a delay
            // Optionally re-fetch users after a delay:
            // setTimeout(() => handleViewUsers(city), 2000);
        }
    } catch (err: unknown) {
        console.error(`Error activating users for ${city}:`, err);
        setError(err instanceof Error ? err.message : `Failed to activate users for ${city}.`);
    } finally {
        setActivatingCity(null); // Clear loading state for this city
    }
  };
  return (
    // Add padding and background consistent with your 'All Users' tab content
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
              Error: {error}
              <button onClick={() => setError(null)} className="ml-4 font-bold">×</button>
          </div>
      )}

      {/* Cities Table Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
         <div className="p-4 flex justify-between items-center border-b">
             <h2 className="text-lg font-semibold text-gray-800">Waitlist by City</h2>
             <button
                onClick={fetchCities}
                className="p-2 text-gray-500 rounded-full hover:bg-gray-100 disabled:opacity-50"
                disabled={loadingCities}
                title="Refresh Cities"
             >
                <RefreshCw className={`h-5 w-5 ${loadingCities ? 'animate-spin' : ''}`} />
             </button>
         </div>
        {loadingCities ? (
            <div className="text-center py-10 text-gray-500">Loading cities...</div>
        ) : (
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Waitlist Count</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {cities.length === 0 && (
                        <tr><td colSpan={3} className="text-center py-6 text-sm text-gray-500">No cities found in waitlist.</td></tr>
                    )}
                    {cities.map(cityInfo => (
                    <tr key={cityInfo._id || 'unknown'} className={`hover:bg-gray-50 ${selectedCity === cityInfo._id ? 'bg-blue-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {cityInfo._id || <span className="text-gray-400 italic">Unknown City</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{cityInfo.count}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                        {/* View Users Button */}
                        <button
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            onClick={() => handleViewUsers(cityInfo._id)}
                            disabled={(!cityInfo._id) || (loadingUsers && selectedCity === cityInfo._id)}
                        >
                            <Users className="h-4 w-4 mr-1" />
                            View Users
                        </button>
                        {/* Activate All Button */}
                        <button
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handleActivateAll(cityInfo._id)}
                            disabled={!cityInfo._id || cityInfo.count === 0 || activatingCity === cityInfo._id}
                        >
                            {activatingCity === cityInfo._id ? (
                                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                                <CheckCircle className="h-4 w-4 mr-1" />
                            )}
                            Activate All ({cityInfo.count})
                        </button>
                        </td>
                    </tr>
                    ))}               
                  </tbody>
                </table>
            </div>
        )}
      </div>

      {/* Selected City Users List Section */}
      {selectedCity && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-700 mb-3">Waitlist Users in "{selectedCity}"</h3>
          {loadingUsers ? (
            <div className="text-center py-4 text-gray-500">
                <RefreshCw className="h-5 w-5 animate-spin inline-block mr-2"/>
                Loading users...
            </div>
          ) : (
            <div className="max-h-60 overflow-y-auto pr-2"> {/* Added padding-right for scrollbar */}
                {users.length === 0 ? (
                     <p className="text-sm text-gray-500 italic">No users found in this city's waitlist.</p>
                ) : (
                    <ul className="space-y-2">
                    {users.map(u => (
                        <li key={u._id} className="text-sm text-gray-800 border-b border-gray-100 pb-1 last:border-b-0">
                        <span className="font-medium">{u.username}</span>
                        <span className="text-gray-500 ml-2">({u.email})</span>
                        </li>
                    ))}
                    </ul>
                )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default WaitlistByCityPanel;
